import { useLocation } from 'react-router-dom';
import { useEffect, useId, useRef, useState } from 'react';

import { followedLinkHref, installHistoryPatch, toPathname } from 'lib/helpers/historyPatch';
import {
  isPrimaryNavigationBlocker,
  registerNavigationBlocker
} from 'lib/helpers/navigationBlockerRegistry';
import {
  allowNextUnload,
  isNavigationGuardSuppressed,
  isUnloadAllowed,
  suppressNavigationGuard
} from 'lib/helpers/navigationGuard';

/**
 * Custom hook to block navigation when there are unsaved changes.
 * Works with BrowserRouter (legacy router) by using beforeunload for browser navigation
 * and intercepting React Router navigation via history API.
 *
 * Nested blockers (a dirty Modal inside a dirty Stepper) all attach their own
 * listeners, so only one of them may act on a given event — see
 * `navigationBlockerRegistry`. `depth` is what orders them.
 *
 * @param {boolean} shouldBlock - Whether navigation should be blocked
 * @param {string} message - Optional custom message for confirmation dialog
 * @param {Object} [options]
 * @param {number} [options.depth=0] - Nesting depth of this blocker's layer; the deepest
 *   armed blocker owns the browser-level guards.
 * @param {(targetPathname: string) => boolean} [options.shouldBlockPath] - Optional predicate applied
 *   to a cross-route target pathname. When provided, a navigation is blocked only if the predicate
 *   returns true — e.g. to allow moving within a subtree while still guarding exits from it. Omit to
 *   block any navigation that leaves the current pathname (the default).
 * @returns {Object} Object with showConfirm state, handlers, and navigation state
 */
const useNavigationBlocker = (shouldBlock, message, { depth = 0, shouldBlockPath } = {}) => {
  const location = useLocation();
  const id = useId();
  const shouldBlockRef = useRef(shouldBlock);
  const locationRef = useRef(location);
  const shouldBlockPathRef = useRef(shouldBlockPath);
  const isNavigatingRef = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  // Pre-patch history methods, kept across re-arms so a confirmed navigation is
  // always executed against the real browser methods, not another blocker's patch.
  const nativeHistoryRef = useRef(null);

  // Keep refs up to date
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
    locationRef.current = location;
    shouldBlockPathRef.current = shouldBlockPath;
  }, [shouldBlock, location, shouldBlockPath]);

  useEffect(() => {
    if (!shouldBlock) return undefined;
    return registerNavigationBlocker(id, depth);
  }, [shouldBlock, id, depth]);

  // Interception is on only while the guard is armed, this blocker owns the
  // guards, we are not already running a navigation the user confirmed, and
  // nothing has asked to be let through (see suppressNavigationGuard).
  const shouldIntercept = () =>
    shouldBlockRef.current &&
    isPrimaryNavigationBlocker(id) &&
    !isNavigatingRef.current &&
    !isNavigationGuardSuppressed();

  // Function to execute pending navigation
  const executePendingNavigation = () => {
    if (pendingNavigationRef.current) {
      const { type, args } = pendingNavigationRef.current;
      const natives = nativeHistoryRef.current;
      isNavigatingRef.current = true;

      if (type === 'pushState' && natives?.pushState) {
        natives.pushState.apply(window.history, args);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else if (type === 'replaceState' && natives?.replaceState) {
        natives.replaceState.apply(window.history, args);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else if (type === 'popstate') {
        window.history.back();
      } else if (type === 'link') {
        const href = args[0];
        if (href) {
          const pushState = natives?.pushState || window.history.pushState;
          pushState.call(window.history, null, '', href);
          // Notify React Router of the navigation without a full page refresh
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } else if (type === 'refresh') {
        allowNextUnload();
        window.location.reload();
        return; // Don't reset flags since page is reloading
      }

      setTimeout(() => {
        isNavigatingRef.current = false;
        pendingNavigationRef.current = null;
      }, 100);
    }
  };

  // Handle keyboard shortcuts for refresh (F5, Ctrl+R, Ctrl+Shift+R)
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleKeyDown = (e) => {
      // Check for refresh shortcuts: F5, Ctrl+R, Ctrl+Shift+R
      const isF5 = e.key === 'F5';
      const isCtrlR = (e.ctrlKey || e.metaKey) && e.key === 'r' && !e.shiftKey;
      const isCtrlShiftR = (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R';

      if (isF5 || isCtrlR || isCtrlShiftR) {
        if (shouldIntercept()) {
          e.preventDefault();
          e.stopPropagation();
          pendingNavigationRef.current = { type: 'refresh', args: [] };
          setShowConfirm(true);
        }
      }
    };

    // Use capture phase to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [shouldBlock]);

  // Handle browser navigation (refresh, close tab, etc.)
  // This is a fallback for when user clicks browser refresh button
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (e) => {
      // Only show browser alert if we haven't already shown our custom modal
      // This happens when user clicks browser refresh button (not keyboard shortcut).
      // A reload the app itself performs (see suppressNavigationGuard) goes through.
      if (
        !showConfirm &&
        isPrimaryNavigationBlocker(id) &&
        !isNavigationGuardSuppressed() &&
        !isUnloadAllowed()
      ) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // Required for Safari
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, showConfirm, id]);

  // Intercept React Router navigation by detecting location changes
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const previousLocation = locationRef.current.pathname + locationRef.current.search;

    // Block a cross-route navigation to `targetPath`? Same-page query-param updates (e.g. DocumentsTable
    // syncing document_id) never block. A caller-supplied shouldBlockPath decides among cross-route
    // targets — letting intra-subtree navigation through while still guarding exits from the subtree.
    const shouldBlockForTarget = (targetPath) => {
      if (targetPath == null || targetPath === locationRef.current.pathname) return false;
      const pred = shouldBlockPathRef.current;
      return typeof pred === 'function' ? pred(targetPath) : true;
    };

    const intercept = (type) => (args) => {
      if (!shouldIntercept() || !shouldBlockForTarget(toPathname(args?.[2]))) return false;
      pendingNavigationRef.current = { type, args };
      setShowConfirm(true);
      return true;
    };

    const { natives, uninstall } = installHistoryPatch({
      natives: nativeHistoryRef.current,
      onPushState: intercept('pushState'),
      onReplaceState: intercept('replaceState')
    });
    nativeHistoryRef.current = natives;

    // popstate (back/forward): the browser already moved, so read the target from window.location and
    // honor shouldBlockPath — else back/forward within an allowed subtree would be blocked. With no
    // predicate this keeps the original always-block behavior.
    const handlePopState = () => {
      if (!shouldIntercept()) return;
      // locationRef still holds the pre-navigation path here, so window.location is the target: without
      // a predicate this blocks (matching the original), with one it respects the allowed subtree.
      if (!shouldBlockForTarget(window.location.pathname)) return;
      // Push current state back to prevent navigation.
      // Set flag before pushState to prevent recursive interception by the patched pushState.
      isNavigatingRef.current = true;
      window.history.pushState(null, '', previousLocation);
      isNavigatingRef.current = false;
      pendingNavigationRef.current = { type: 'popstate', args: [] };
      setShowConfirm(true);
    };

    // Bubble phase, after the app's own handlers: a link handled in JS (a router Link, a dock tab)
    // prevents the default and navigates through history, where the patch above holds it with its
    // router state. Only a link the browser itself would follow is held here, by its href.
    const handleLinkClick = (e) => {
      if (!shouldIntercept()) return;
      const href = followedLinkHref(e);
      if (!href || !shouldBlockForTarget(toPathname(href))) return;
      e.preventDefault();
      pendingNavigationRef.current = { type: 'link', args: [href] };
      setShowConfirm(true);
    };

    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleLinkClick);

    return () => {
      uninstall();
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [shouldBlock, message]);

  const handleConfirm = () => {
    setShowConfirm(false);
    const pending = pendingNavigationRef.current;

    // If it's a custom callback (modal close, etc.), execute it
    if (pending?.type === 'custom') {
      // Suppress interception while the confirmed action runs — it may
      // navigate (e.g. discarding changes before a Stepper substep change)
      // while the dirty state that triggered this confirm is still set, so
      // without this the just-confirmed navigation would re-open the modal.
      suppressNavigationGuard(() => pending.callback?.());
    } else {
      // Otherwise, execute navigation. Suppressed for the same reason as the
      // custom branch: the confirmed navigation runs while this guard's dirty
      // state is still set, and `natives` may be another blocker's patch rather
      // than the browser's own method, which would re-intercept it.
      suppressNavigationGuard(() => executePendingNavigation());
    }

    pendingNavigationRef.current = null;
  };

  const handleCancel = () => {
    const pendingType = pendingNavigationRef.current?.type;
    setShowConfirm(false);
    pendingNavigationRef.current = null;
    // For popstate, we need to push the current state back to prevent navigation
    if (pendingType === 'popstate') {
      // Set flag before pushState to prevent recursive interception by overridden pushState
      isNavigatingRef.current = true;
      window.history.pushState(null, '', locationRef.current.pathname + locationRef.current.search);
      isNavigatingRef.current = false;
    }
  };

  // Function to trigger confirmation for custom close attempts (modals, etc.)
  // Accepts isDirty parameter to check current state instead of relying on ref
  const triggerConfirm = (callback, isDirty = null) => {
    // Use provided isDirty if available, otherwise fall back to ref
    const shouldBlockNow = isDirty !== null ? isDirty : shouldBlockRef.current;

    if (shouldBlockNow && !isNavigatingRef.current) {
      pendingNavigationRef.current = { type: 'custom', callback };
      setShowConfirm(true);
    } else if (callback) {
      // If not blocking, execute callback immediately
      callback();
    }
  };

  const getPendingType = () => pendingNavigationRef.current?.type;

  return {
    showConfirm,
    handleConfirm,
    handleCancel,
    triggerConfirm,
    getPendingType,
    message
  };
};

export default useNavigationBlocker;
